import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ExclamationTriangleIcon,
    XCircleIcon,
    CheckCircleIcon,
    CreditCardIcon,
} from '@heroicons/react/24/outline';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import type { ValidationResult } from '@/lib/api/import';

interface ImportPreviewDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Validation result from the import validation API */
    validationResult: ValidationResult;
    /** Callback when user confirms the import */
    onConfirm: () => void;
    /** Callback when user cancels */
    onCancel: () => void;
    /** Whether the import is currently processing */
    isProcessing: boolean;
}

/**
 * ImportPreviewDialog shows a summary of what will be imported,
 * the credit cost breakdown, and requires user confirmation.
 */
export function ImportPreviewDialog({
    open,
    validationResult,
    onConfirm,
    onCancel,
    isProcessing,
}: ImportPreviewDialogProps) {
    const [confirmed, setConfirmed] = useState(false);

    const { summary, creditCost, userBalance, hasSufficientCredits, balanceAfterImport, warnings } =
        validationResult;

    const hasItemsToCreate =
        summary.ledgersToCreate > 0 ||
        summary.valuesToCreate > 0 ||
        summary.returnsToCreate > 0;

    const hasItemsToSkip = summary.valuesToSkip > 0 || summary.returnsToSkip > 0;

    // Reset confirmation when dialog closes
    const handleAfterLeave = () => {
        setConfirmed(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            title="Import Preview"
            size="lg"
            afterLeave={handleAfterLeave}
            dismissible={!isProcessing}
        >
            <div className="space-y-6">
                {/* Insufficient Credits Error */}
                {!hasSufficientCredits && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-red-800 dark:text-red-300">
                                    Insufficient Credits
                                </p>
                                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                                    You need {creditCost.total} credits but only have {userBalance}.{' '}
                                    <Link
                                        to="/credits"
                                        className="font-medium underline hover:no-underline"
                                    >
                                        Purchase more credits
                                    </Link>{' '}
                                    to continue.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Import Summary */}
                <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Import Summary
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        {hasItemsToCreate ? (
                            <div className="space-y-2">
                                <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                    <span className="font-medium">Items to create:</span>
                                </p>
                                <ul className="ml-6 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                    {summary.ledgersToCreate > 0 && (
                                        <li>{summary.ledgersToCreate} ledgers</li>
                                    )}
                                    {summary.valuesToCreate > 0 && (
                                        <li>{summary.valuesToCreate} values</li>
                                    )}
                                    {summary.returnsToCreate > 0 && (
                                        <li>{summary.returnsToCreate} returns</li>
                                    )}
                                </ul>
                            </div>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400">No new items to import</p>
                        )}

                        {hasItemsToSkip && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <p className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                    <span className="font-medium">Items to skip:</span>
                                </p>
                                <ul className="ml-6 mt-1 space-y-1 text-sm text-gray-500 dark:text-gray-400">
                                    {summary.valuesToSkip > 0 && (
                                        <li>{summary.valuesToSkip} values (duplicates)</li>
                                    )}
                                    {summary.returnsToSkip > 0 && (
                                        <li>{summary.returnsToSkip} returns (duplicates)</li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {/* Credit Cost Breakdown */}
                <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                        <CreditCardIcon className="h-5 w-5" />
                        Credit Cost
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="space-y-2 text-sm">
                            {creditCost.ledgers > 0 && (
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                    <span>Ledgers ({summary.ledgersToCreate} x 2)</span>
                                    <span>{creditCost.ledgers}</span>
                                </div>
                            )}
                            {creditCost.values > 0 && (
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                    <span>Values ({summary.valuesToCreate} x 1)</span>
                                    <span>{creditCost.values}</span>
                                </div>
                            )}
                            {creditCost.returns > 0 && (
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                    <span>Returns ({summary.returnsToCreate} x 1)</span>
                                    <span>{creditCost.returns}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-medium text-gray-900 dark:text-gray-100 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <span>Total Cost</span>
                                <span>{creditCost.total} credits</span>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2 text-sm">
                            <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                <span>Current Balance</span>
                                <span>{userBalance} credits</span>
                            </div>
                            <div
                                className={`flex justify-between font-medium ${
                                    hasSufficientCredits
                                        ? 'text-gray-900 dark:text-gray-100'
                                        : 'text-red-600 dark:text-red-400'
                                }`}
                            >
                                <span>Balance After Import</span>
                                <span>{balanceAfterImport} credits</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Warnings */}
                {warnings.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-yellow-800 dark:text-yellow-300">
                                    Warnings
                                </p>
                                <ul className="mt-1 text-sm text-yellow-700 dark:text-yellow-400 list-disc list-inside">
                                    {warnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirmation Checkbox */}
                {hasSufficientCredits && hasItemsToCreate && (
                    <div className="pt-2">
                        <Checkbox
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            label="I understand this will deduct credits and create the items listed above"
                            disabled={isProcessing}
                        />
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-2">
                    <Button variant="secondary" onClick={onCancel} disabled={isProcessing}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onConfirm}
                        disabled={!hasSufficientCredits || !confirmed || !hasItemsToCreate}
                        isLoading={isProcessing}
                    >
                        Import
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
